/**
 * Sign-in screen (P0-BR2, NFR-1).
 *
 * Designed for a mid-range Android phone held in one hand: 360px width, large
 * targets, a numeric keypad for the phone number, and nothing above the fold
 * that is not needed to sign in.
 */

import { useMemo, useState, type FormEvent } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { LOCALE_LABELS, translatorFor, type Locale } from '../../i18n/index.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { useAuth } from './AuthContext.js';
import { DevQuickLogin } from './DevQuickLogin.js';

export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const { t, locale } = useTranslation();

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Before sign-in there is no stored preference, so the toggle is local. */
  const [previewLocale, setPreviewLocale] = useState<Locale | null>(null);

  const activeLocale = previewLocale ?? locale;
  // Rebuilt from the active locale, so tapping the toggle actually changes the
  // strings rather than only the highlight.
  const translate = useMemo(() => (previewLocale ? translatorFor(previewLocale) : t), [previewLocale, t]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setIsSubmitting(true);

    try {
      await login(phone, password);
    } catch (error) {
      setErrorKey(errorMessageKey(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-5 py-8">
      <h1 className="mb-1 text-2xl font-semibold text-green-800">{translate('app.name')}</h1>
      <p className="mb-8 text-sm text-gray-600">{translate('auth.login.title')}</p>

      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="phone">
          {translate('auth.login.phone')}
        </label>
        <input
          id="phone"
          name="phone"
          // A phone keypad rather than a full keyboard: the login id is digits.
          type="tel"
          inputMode="numeric"
          autoComplete="username"
          placeholder={translate('auth.login.phonePlaceholder')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="mb-5 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3 text-lg"
        />

        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="password">
          {translate('auth.login.password')}
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-6 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3 text-lg"
        />

        {errorKey !== null && (
          // role=alert so a screen reader announces it, and so the test can
          // find it without depending on the wording.
          <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
            {translate(errorKey)}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="min-h-touch w-full rounded-lg bg-green-700 px-4 py-3 text-lg font-medium text-white disabled:opacity-60"
        >
          {translate(isSubmitting ? 'auth.login.signingIn' : 'auth.login.submit')}
        </button>
      </form>

      {/* Development only; compiled out of production builds entirely. */}
      <DevQuickLogin onPick={login} />

      <div className="mt-8 flex justify-center gap-2">
        {(Object.keys(LOCALE_LABELS) as Locale[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setPreviewLocale(option)}
            aria-pressed={activeLocale === option}
            className={`min-h-touch min-w-touch rounded-lg px-4 py-2 text-sm ${
              activeLocale === option ? 'bg-green-100 font-medium text-green-900' : 'text-gray-600'
            }`}
          >
            {LOCALE_LABELS[option]}
          </button>
        ))}
      </div>
    </main>
  );
}
